import { useCallback, useEffect, useMemo, useState } from "react";
import { Refine, Authenticated } from "@refinedev/core";
import {
  ErrorComponent,
  ThemedLayoutV2,
  useNotificationProvider,
} from "@refinedev/antd";
import routerBindings, {
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router-v6";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { ConfigProvider, App as AntdApp } from "antd";
import zhCN from "antd/locale/zh_CN";
import "@refinedev/antd/dist/reset.css";
import { DashboardOutlined } from "@ant-design/icons";

import { dataProvider } from "@refinedev/supabase";
import { supabaseClient } from "./utility/supabaseClient";
import authProvider from "./authProvider";
import { getTheme, ThemeMode } from "./theme";

// Resource pages
import { LoginPage } from "./pages/auth";
import { MovieList, MovieCreate, MovieEdit, MovieShow } from "./pages/movies";
import { CategoryList, CategoryCreate, CategoryEdit } from "./pages/categories";
import { RedemptionCodeList, RedemptionCodeShow } from "./pages/redemption-codes";
import { MembershipTierList, MembershipTierCreate, MembershipTierEdit } from "./pages/membership-tiers";
import { UserList } from "./pages/users";
import { AdminHeader } from "./components/layout/AdminHeader";
import { AdminSider } from "./components/layout/AdminSider";
import { DashboardOverview } from "./pages/dashboard";

const THEME_STORAGE_KEY = "yeguangbaohe-admin-theme";

function App() {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    if (typeof window === "undefined") {
      return "light";
    }
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode | null;
    return stored ?? "light";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    }
  }, [themeMode]);

  const handleThemeChange = useCallback((mode: ThemeMode) => {
    setThemeMode(mode);
  }, []);

  const themeConfig = useMemo(() => getTheme(themeMode), [themeMode]);

  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <ConfigProvider locale={zhCN} theme={themeConfig}>
        <AntdApp>
          <Refine
              dataProvider={dataProvider(supabaseClient)}
              authProvider={authProvider}
              routerProvider={routerBindings}
              notificationProvider={useNotificationProvider}
              resources={[
                {
                  name: "dashboard",
                  list: "/dashboard",
                  icon: <DashboardOutlined />,
                  meta: {
                    canDelete: false,
                    label: "仪表盘",
                  },
                },
                {
                  name: "movies",
                  list: "/movies",
                  create: "/movies/create",
                  edit: "/movies/edit/:id",
                  show: "/movies/show/:id",
                  meta: {
                    canDelete: true,
                    label: "电影管理",
                  },
                },
                {
                  name: "categories",
                  list: "/categories",
                  create: "/categories/create",
                  edit: "/categories/edit/:id",
                  meta: {
                    canDelete: true,
                    label: "分类管理",
                  },
                },
                {
                  name: "redemption_codes",
                  list: "/redemption-codes",
                  show: "/redemption-codes/show/:id",
                  meta: {
                    label: "兑换码管理",
                  },
                },
                {
                  name: "membership_tiers",
                  list: "/membership-tiers",
                  create: "/membership-tiers/create",
                  edit: "/membership-tiers/edit/:id",
                  meta: {
                    canDelete: true,
                    label: "订阅等级",
                  },
                },
                {
                  name: "users",
                  list: "/users",
                  meta: {
                    label: "用户管理",
                  },
                },
              ]}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
                projectId: "xingmu-cinema-admin",
              }}
            >
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route
                  element={
                    <Authenticated
                      key="protected-layout"
                      fallback={<Navigate to="/login" replace />}
                    >
                      <ThemedLayoutV2
                        Header={() => (
                          <AdminHeader
                            themeMode={themeMode}
                            onThemeChange={handleThemeChange}
                          />
                        )}
                        Sider={() => <AdminSider themeMode={themeMode} />}
                      >
                        <Outlet />
                      </ThemedLayoutV2>
                    </Authenticated>
                  }
                >
                  <Route
                    index
                    element={<NavigateToResource resource="dashboard" />}
                  />

                  <Route path="/dashboard" element={<DashboardOverview />} />

                  {/* Movies Routes */}
                  <Route path="/movies">
                    <Route index element={<MovieList />} />
                    <Route path="create" element={<MovieCreate />} />
                    <Route path="edit/:id" element={<MovieEdit />} />
                    <Route path="show/:id" element={<MovieShow />} />
                  </Route>

                  {/* Categories Routes */}
                  <Route path="/categories">
                    <Route index element={<CategoryList />} />
                    <Route path="create" element={<CategoryCreate />} />
                    <Route path="edit/:id" element={<CategoryEdit />} />
                  </Route>

                  {/* Redemption Codes Routes */}
                  <Route path="/redemption-codes">
                    <Route index element={<RedemptionCodeList />} />
                    <Route path="show/:id" element={<RedemptionCodeShow />} />
                  </Route>

                  {/* Membership Tiers Routes */}
                  <Route path="/membership-tiers">
                    <Route index element={<MembershipTierList />} />
                    <Route path="create" element={<MembershipTierCreate />} />
                    <Route path="edit/:id" element={<MembershipTierEdit />} />
                  </Route>

                  {/* Users Routes */}
                  <Route path="/users">
                    <Route index element={<UserList />} />
                  </Route>

                  <Route path="*" element={<ErrorComponent />} />
                </Route>
              </Routes>

              <UnsavedChangesNotifier />
              <DocumentTitleHandler />
            </Refine>
        </AntdApp>
      </ConfigProvider>
    </BrowserRouter>
  );
}

export default App;
